import { create } from "zustand"

import Toast from "@/components/Toast"
import { getWebcmsProgress } from "@/services/core"
import signImg from "@/assets/imgs/letter/sign.png"

export enum GlobalStage {
    LOADING,
    MAIN,
}

export interface CollectionItem {
    key: string
    ts: number
}

interface States {
    player: null | {
        state: {
            avatar?: string
        }
        collection: null | Record<string, CollectionItem[] | null>
    }
    globalStage: GlobalStage
    loadingDisplayProgress: number
    backgroundMapIdleMotionEnabled: boolean
    percent: number
    letter?: { to: string; contents: string[]; signature: string }
    gameStartTs: number
}
interface Actions {
    setPlayer: (payload: States["player"]) => void
    setGlobalStage: (payload: States["globalStage"]) => void
    setLoadingDisplayProgress: (progress: number) => void
    resetLoadingDisplayProgress: () => void
    setBackgroundMapIdleMotionEnabled: (enabled: boolean) => void
    resetBackgroundMapIdleMotionEnabled: () => void
    fetchMeta: () => Promise<void>
    setGameStartTs: (ts: number) => void
    updateCollectionGroup: (group: string, list: CollectionItem[]) => void
}

export type GlobalStore = States & Actions

export const useGlobalStore = create<GlobalStore>((set) => ({
    player: null,
    setPlayer: (payload) => {
        set({ player: payload })
    },
    globalStage: GlobalStage.LOADING,
    setGlobalStage: (payload) => {
        set({ globalStage: payload })
    },
    loadingDisplayProgress: 0,
    setLoadingDisplayProgress: (progress) => {
        set({ loadingDisplayProgress: Math.min(Math.max(progress, 0), 1) })
    },
    resetLoadingDisplayProgress: () => {
        set({ loadingDisplayProgress: 0 })
    },
    backgroundMapIdleMotionEnabled: true,
    setBackgroundMapIdleMotionEnabled: (enabled) => {
        set({ backgroundMapIdleMotionEnabled: enabled })
    },
    resetBackgroundMapIdleMotionEnabled: () => {
        set({ backgroundMapIdleMotionEnabled: true })
    },
    percent: 0,
    letter: {
        to: "ドクターへ",
        contents: [
            "このメッセージを見つけたということは、君はすでにMon3trの記憶領域を漁ることに多大な時間を費やしているのだろう。ロドスが現在直面している難題ゆえにそうせざるを得ないのかもしれないが、私としては適切な運動と十分な睡眠をとることを推奨する――記憶の中から答えを探すよりも、その方がよほど有益だからだ。\n私がこのメッセージを残したのは、今これを読んでいる君に少しばかりの慰めをもたらすことができればと願ったからだ。\n\n君はもう覚えていないだろう話だ。\nこの宇宙はかつて輝かしい文明を育んだ。その時代、知識の境界はほぼ宇宙そのものに等しかった。人の想像し得ることがそのまま認知の形成範囲となり、最も遠い恒星でさえ、机上の書物のように手を伸ばせば触れることができた。\nだがそれでもなお、人々は自身に関する疑問に答えることができずにいた――生命には、生まれながらにして何らかの目的が存在するのだろうか？\nそれは造物主が残した最後の帳なのかもしれない。意義の探求というトンネルに終わりはなく、生命はそれぞれの旅路において孤独へと向かうことが定められている。\n賢人や学者たちは最終的に、観測可能な世界から答えを導き出すことを諦め、我々の本能に信念を託すことを選んだ。\nなぜなら、解のない孤独と困惑の外側において、愛の存在もまた疑いようのないものであると信じるほかなかったからだ。\nどれほど深い絶望であっても、人々が茫々たる星の海で自己存在の反響を探すことを止められはしない。静寂が訪れるその前まで、人々は数万光年彼方の生命の痕跡のために涙を流すのだ。\n旅の途上で、私はようやくその感動を理解したのだと思う。生命は美しい。宇宙の尺度においては、「生命」と「希望」は同義なのだ。",
            "過去の記憶を君に返すことは不可能であるから、こうして私の感想を伝えることしかできない。私は君の本質を信じよう。そして君もまた、自身が選んだことの意味を信じてほしい。\n君はこの先、無力感を覚え、迷いを感じるだろう。永遠に君と共に歩める者はおらず、君の孤独を真に理解できる者もいないのだから。\nロドスは多くの人々の居場所となった。その時が来ても、ロドスが君の帰る場所であることを願っている。\n私の願い、それは君の願いを守ることだ。この旅の終わりに、誰かが君に寄り添い、君が待ち望んだ未来を見届けてくれることを祈っている。",
        ],
        signature: signImg,
    },
    fetchMeta: async () => {
        const res = await getWebcmsProgress()
        if (res.code === 0) {
            const percent = res.data.results.rows[0]?.percent ?? 0
            set({ percent })
        }
    },
    gameStartTs: 0,
    setGameStartTs: (ts) => {
        set({ gameStartTs: ts })
    },
    updateCollectionGroup: (group, list) => {
        set((state) => {
            if (!state.player) return {}
            return {
                player: {
                    ...state.player,
                    collection: {
                        ...state.player.collection,
                        [group]: list,
                    },
                },
            }
        })
    },
}))

export const selectPlayer = (state: GlobalStore) => state.player
export const selectSetPlayer = (state: GlobalStore) => state.setPlayer
export const selectGlobalStage = (state: GlobalStore) => state.globalStage
export const selectSetGlobalStage = (state: GlobalStore) => state.setGlobalStage

export const usePlayer = () => useGlobalStore(selectPlayer)
export const useSetPlayer = () => useGlobalStore(selectSetPlayer)
export const selectPercent = (state: GlobalStore) => state.percent
export const selectFetchMeta = (state: GlobalStore) => state.fetchMeta
export const selectLetter = (state: GlobalStore) => state.letter

export const useGlobalStage = () => useGlobalStore(selectGlobalStage)
export const useSetGlobalStage = () => useGlobalStore(selectSetGlobalStage)
export const usePercent = () => useGlobalStore(selectPercent)
export const useFetchMeta = () => useGlobalStore(selectFetchMeta)
export const useLetter = () => useGlobalStore(selectLetter)

export const requireLogin = (): boolean => {
    const { player, setGlobalStage } = useGlobalStore.getState()
    if (player) return true
    setGlobalStage(GlobalStage.LOADING)
    Toast.message("先にログインしてください")
    return false
}
